import { useEffect, useState } from 'react';
import { ArrowDown, ArrowRight, ArrowUp, Pencil, PinOff, Plus, RotateCcw, Trash2 } from 'lucide-react';
import type {
  CanvasMetadata,
  CheckConstraintModel,
  ColumnModel,
  ForeignKeyModel,
  Group,
  IndexModel,
  Note,
  NoteAttachment,
  PrimaryKeyModel,
  SchemaModel,
  TableModel,
  UniqueConstraintModel,
} from '@bits2schema/shared';
import { getEdgeRoute } from '@bits2schema/shared';
import {
  createDeleteNoteCommand,
  createEditNoteTextCommand,
  createNoteCommand,
  createPinNoteCommand,
  createPlaceTableCommand,
  createRenameGroupCommand,
  createResetEdgeRouteCommand,
  randomId,
  type Command as CanvasCommand,
} from '../canvas/commands';
import { TABLE_NODE_WIDTH, estimateTableNodeHeight, fallbackTablePosition } from '../canvas/geometry';
import { computePlacementsForNewConnection } from '../canvas/layout';
import { useCanvasStore } from '../canvas/store';
import { useCommandStack } from '../command/stack';
import { useT } from '../i18n';
import {
  ColumnTypeEditor,
  type ColumnTypeValue,
} from '../schema/ColumnTypeEditor';
import type { ColumnEditableFields, SchemaCommand } from '../schema/commands';
import {
  createAddCheckConstraintCommand,
  createAddColumnCommand,
  createAddForeignKeyCommand,
  createAddIndexCommand,
  createAddUniqueConstraintCommand,
  createDeleteCheckConstraintCommand,
  createDeleteForeignKeyCommand,
  createDeleteIndexCommand,
  createDeleteUniqueConstraintCommand,
  createMoveColumnCommand,
  createRenameColumnCommand,
  createRenameTableCommand,
  createSetPrimaryKeyCommand,
  createUpdateCheckConstraintCommand,
  createUpdateColumnCommand,
  createUpdateForeignKeyCommand,
  createUpdateIndexCommand,
  createUpdateTableCommentCommand,
  createUpdateUniqueConstraintCommand,
} from '../schema/commands';
import { createDefaultColumn } from '../schema/defaults';
import { computeForeignKeyCardinality } from '../schema/mutations';
import { CheckConstraintDialog } from '../schema/dialogs/CheckConstraintDialog';
import { DeleteColumnDialog } from '../schema/dialogs/DeleteColumnDialog';
import { DeleteTableDialog } from '../schema/dialogs/DeleteTableDialog';
import { ForeignKeyDialog, type ForeignKeyDialogResult } from '../schema/dialogs/ForeignKeyDialog';
import { IndexDialog } from '../schema/dialogs/IndexDialog';
import { UniqueConstraintDialog } from '../schema/dialogs/UniqueConstraintDialog';
import { useSchemaStore } from '../schema/store';
import { useTypeCatalog } from '../schema/typeCatalog';
import { useWorkspaceSelection } from './selection';

/**
 * Pravý panel (E11-US5) — kontejner, který se přepíná podle `inspectorTarget`.
 * Od Epic 4 je editace struktury (tabulka/sloupec/constrainty) plně
 * funkční přes `useSchemaStore()`; poznámky a skupiny (Epic 6) zůstávají na
 * `useCanvasStore()`, jsou to dvě oddělené command vrstvy (viz
 * `schema/store.tsx` hlavička).
 */
export function Inspector() {
  const t = useT();
  const { inspectorTarget } = useWorkspaceSelection();
  const { schema, execute: schemaExecute } = useSchemaStore();
  const { canvas, execute } = useCanvasStore();

  const content = resolveContent(inspectorTarget, schema, canvas, execute, schemaExecute);

  return (
    <div className="inspector">
      <h2 className="inspector__title">{t('inspector.title')}</h2>
      {content ?? <p className="hint inspector__empty">{t('inspector.empty')}</p>}
    </div>
  );
}

function resolveContent(
  target: ReturnType<typeof useWorkspaceSelection>['inspectorTarget'],
  schema: SchemaModel,
  canvas: CanvasMetadata,
  execute: (command: CanvasCommand) => void,
  schemaExecute: (command: SchemaCommand) => void,
) {
  if (target.kind === 'table') {
    const table = schema.tables.find((candidate) => candidate.id === target.tableId);
    return table ? (
      <TableInspector table={table} schema={schema} canvas={canvas} execute={execute} schemaExecute={schemaExecute} />
    ) : null;
  }
  if (target.kind === 'column') {
    const table = schema.tables.find((candidate) => candidate.id === target.tableId);
    const column = table?.columns.find((candidate) => candidate.id === target.columnId);
    return table && column ? (
      <ColumnInspector
        table={table}
        column={column}
        schema={schema}
        canvas={canvas}
        execute={execute}
        schemaExecute={schemaExecute}
      />
    ) : null;
  }
  if (target.kind === 'group') {
    const group = canvas.groups.find((candidate) => candidate.id === target.groupId);
    return group ? <GroupInspector group={group} schema={schema} canvas={canvas} execute={execute} /> : null;
  }
  if (target.kind === 'note') {
    const note = canvas.notes.find((candidate) => candidate.id === target.noteId);
    return note ? <NoteInspector note={note} schema={schema} canvas={canvas} execute={execute} /> : null;
  }
  if (target.kind === 'foreignKey') {
    const table = schema.tables.find((candidate) => candidate.id === target.tableId);
    const foreignKey = table?.foreignKeys.find((candidate) => candidate.id === target.foreignKeyId);
    return table && foreignKey ? (
      <ForeignKeyInspector
        table={table}
        foreignKey={foreignKey}
        schema={schema}
        canvas={canvas}
        execute={execute}
        schemaExecute={schemaExecute}
      />
    ) : null;
  }
  return null;
}

function findColumnLabel(schema: SchemaModel, columnId: string): string | null {
  for (const table of schema.tables) {
    const column = table.columns.find((candidate) => candidate.id === columnId);
    if (column) return `${table.name}.${column.name}`;
  }
  return null;
}

function isPkColumn(table: TableModel, column: ColumnModel): boolean {
  return table.primaryKey?.columns.includes(column.name) ?? false;
}

interface InspectorContentProps {
  canvas: CanvasMetadata;
  execute: (command: CanvasCommand) => void;
}

interface SchemaInspectorProps extends InspectorContentProps {
  schema: SchemaModel;
  schemaExecute: (command: SchemaCommand) => void;
}

function TableInspector({ table, schema, canvas, execute, schemaExecute }: SchemaInspectorProps & { table: TableModel }) {
  const t = useT();
  const { setInspectorTarget } = useWorkspaceSelection();
  const notes = canvas.notes.filter((note) => note.attachment.type === 'table' && note.attachment.id === table.id);
  const sortedColumns = [...table.columns].sort((a, b) => a.ordinal - b.ordinal);

  const [name, setName] = useState(table.name);
  useEffect(() => setName(table.name), [table.id, table.name]);
  const [nameError, setNameError] = useState<string | null>(null);
  const [comment, setComment] = useState(table.comment ?? '');
  useEffect(() => setComment(table.comment ?? ''), [table.id, table.comment]);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const commitName = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(t('schema.validation.emptyName'));
      setName(table.name);
      return;
    }
    if (trimmed === table.name) {
      setNameError(null);
      return;
    }
    const duplicate = schema.tables.some(
      (candidate) => candidate.id !== table.id && candidate.schema === table.schema && candidate.name === trimmed,
    );
    if (duplicate) {
      setNameError(t('inspector.table.duplicateName', { name: trimmed }));
      setName(table.name);
      return;
    }
    setNameError(null);
    schemaExecute(createRenameTableCommand(table.id, table.name, trimmed, table.renamedFrom));
  };

  const commitComment = () => {
    const next = comment.trim() ? comment.trim() : null;
    if (next !== table.comment) schemaExecute(createUpdateTableCommentCommand(table.id, table.comment, next));
  };

  const addColumn = () => {
    const column = createDefaultColumn(table, schema.dialect);
    schemaExecute(createAddColumnCommand(table.id, column));
    setInspectorTarget({ kind: 'column', tableId: table.id, columnId: column.id });
  };

  return (
    <div className="inspector__body">
      <div className="field">
        <label htmlFor="inspector-table-name">{t('inspector.table.nameLabel')}</label>
        <input
          id="inspector-table-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={commitName}
          onKeyDown={(event) => {
            if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
          }}
        />
        {nameError && <p className="notice notice--error">{nameError}</p>}
      </div>
      <dl className="inspector__meta">
        <div>
          <dt>{t('inspector.table.schema')}</dt>
          <dd>{table.schema}</dd>
        </div>
      </dl>

      <div className="inspector__section">
        <h4>{t('inspector.section.comment')}</h4>
        <textarea value={comment} onChange={(event) => setComment(event.target.value)} onBlur={commitComment} rows={2} />
      </div>

      <div className="inspector__section">
        <h4>{t('inspector.table.columnsTitle', { count: sortedColumns.length })}</h4>
        <ul className="inspector__column-list">
          {sortedColumns.map((column, index) => (
            <li key={column.id}>
              <button
                type="button"
                className="inspector__column-link"
                onClick={() => setInspectorTarget({ kind: 'column', tableId: table.id, columnId: column.id })}
              >
                <span className={isPkColumn(table, column) ? 'inspector__column-name inspector__column-name--pk' : 'inspector__column-name'}>
                  {column.name}
                </span>
                <span className="hint">{column.canonicalType ?? column.rawType}</span>
              </button>
              {/* Epic 15, E15-US5 — prohození se sousedem podle `ordinal`; nikdy neovlivní
                  sync/change script (`ordinal` mimo `diff.ts#columnEqual`, viz epic-15 kap. 2). */}
              <div className="inspector__column-move">
                <button
                  type="button"
                  className="icon-button"
                  disabled={index === 0}
                  title={t('inspector.column.moveUp')}
                  onClick={() => schemaExecute(createMoveColumnCommand(table.id, column.id, column.name, 'up'))}
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  disabled={index === sortedColumns.length - 1}
                  title={t('inspector.column.moveDown')}
                  onClick={() => schemaExecute(createMoveColumnCommand(table.id, column.id, column.name, 'down'))}
                >
                  <ArrowDown size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
        <button type="button" onClick={addColumn}>
          <Plus size={16} />
          {t('inspector.table.addColumn')}
        </button>
      </div>

      <PrimaryKeySection table={table} />
      <UniqueConstraintsSection table={table} schemaExecute={schemaExecute} />
      <CheckConstraintsSection table={table} schemaExecute={schemaExecute} />
      <ForeignKeysSection table={table} schema={schema} canvas={canvas} execute={execute} schemaExecute={schemaExecute} />
      <IndexesSection table={table} schemaExecute={schemaExecute} />

      <div className="inspector__section">
        <button type="button" className="danger" onClick={() => setDeleteOpen(true)}>
          <Trash2 size={16} />
          {t('inspector.table.deleteButton')}
        </button>
      </div>

      <NotesSection notes={notes} canvas={canvas} execute={execute} attachment={{ type: 'table', id: table.id }} />

      {deleteOpen && (
        <DeleteTableDialog
          table={table}
          schema={schema}
          canvas={canvas}
          schemaExecute={schemaExecute}
          canvasExecute={execute}
          onClose={() => setDeleteOpen(false)}
        />
      )}
    </div>
  );
}

function PrimaryKeySection({ table }: { table: TableModel }) {
  const t = useT();
  return (
    <div className="inspector__section">
      <h4>{t('inspector.table.primaryKey')}</h4>
      <p className="hint">{table.primaryKey ? table.primaryKey.columns.join(', ') : t('inspector.table.none')}</p>
    </div>
  );
}

function UniqueConstraintsSection({ table, schemaExecute }: { table: TableModel; schemaExecute: (command: SchemaCommand) => void }) {
  const t = useT();
  const [dialog, setDialog] = useState<{ mode: 'create' } | { mode: 'edit'; constraint: UniqueConstraintModel } | null>(null);

  return (
    <div className="inspector__section">
      <h4>{t('inspector.table.uniqueTitle', { count: table.uniqueConstraints.length })}</h4>
      <ul className="inspector__column-list">
        {table.uniqueConstraints.map((constraint, index) => (
          <li key={constraint.id}>
            <div>
              <span className="inspector__column-name">{constraint.name}</span>
              <span className="hint">{constraint.columns.join(', ')}</span>
            </div>
            <div className="toolbar__actions">
              <button type="button" onClick={() => setDialog({ mode: 'edit', constraint })}>
                <Pencil size={16} />
                {t('inspector.table.edit')}
              </button>
              <button
                type="button"
                className="danger"
                onClick={() => schemaExecute(createDeleteUniqueConstraintCommand(table.id, constraint, index))}
              >
                <Trash2 size={16} />
                {t('common.delete')}
              </button>
            </div>
          </li>
        ))}
      </ul>
      <button type="button" onClick={() => setDialog({ mode: 'create' })}>
        <Plus size={16} />
        {t('inspector.table.addUnique')}
      </button>
      {dialog && (
        <UniqueConstraintDialog
          table={table}
          initial={dialog.mode === 'edit' ? dialog.constraint : null}
          onCancel={() => setDialog(null)}
          onConfirm={(columns, name) => {
            if (dialog.mode === 'edit') {
              schemaExecute(
                createUpdateUniqueConstraintCommand(table.id, dialog.constraint.id, dialog.constraint, {
                  ...dialog.constraint,
                  columns,
                  name,
                }),
              );
            } else {
              schemaExecute(createAddUniqueConstraintCommand(table.id, { id: randomId(), name, columns }));
            }
            setDialog(null);
          }}
        />
      )}
    </div>
  );
}

function CheckConstraintsSection({ table, schemaExecute }: { table: TableModel; schemaExecute: (command: SchemaCommand) => void }) {
  const t = useT();
  const [dialog, setDialog] = useState<{ mode: 'create' } | { mode: 'edit'; constraint: CheckConstraintModel } | null>(null);

  return (
    <div className="inspector__section">
      <h4>{t('inspector.table.checkTitle', { count: table.checkConstraints.length })}</h4>
      <ul className="inspector__column-list">
        {table.checkConstraints.map((constraint, index) => (
          <li key={constraint.id}>
            <div>
              <span className="inspector__column-name">{constraint.name}</span>
              <span className="hint">{constraint.expression}</span>
            </div>
            <div className="toolbar__actions">
              <button type="button" onClick={() => setDialog({ mode: 'edit', constraint })}>
                <Pencil size={16} />
                {t('inspector.table.edit')}
              </button>
              <button
                type="button"
                className="danger"
                onClick={() => schemaExecute(createDeleteCheckConstraintCommand(table.id, constraint, index))}
              >
                <Trash2 size={16} />
                {t('common.delete')}
              </button>
            </div>
          </li>
        ))}
      </ul>
      <button type="button" onClick={() => setDialog({ mode: 'create' })}>
        <Plus size={16} />
        {t('inspector.table.addCheck')}
      </button>
      {dialog && (
        <CheckConstraintDialog
          table={table}
          initial={dialog.mode === 'edit' ? dialog.constraint : null}
          onCancel={() => setDialog(null)}
          onConfirm={(expression, name) => {
            if (dialog.mode === 'edit') {
              schemaExecute(
                createUpdateCheckConstraintCommand(table.id, dialog.constraint.id, dialog.constraint, {
                  ...dialog.constraint,
                  expression,
                  name,
                }),
              );
            } else {
              schemaExecute(createAddCheckConstraintCommand(table.id, { id: randomId(), name, expression }));
            }
            setDialog(null);
          }}
        />
      )}
    </div>
  );
}

function ForeignKeysSection({
  table,
  schema,
  canvas,
  execute,
  schemaExecute,
}: {
  table: TableModel;
  schema: SchemaModel;
  canvas: CanvasMetadata;
  execute: (command: CanvasCommand) => void;
  schemaExecute: (command: SchemaCommand) => void;
}) {
  const t = useT();
  const { runGroup } = useCommandStack();
  const [dialog, setDialog] = useState<{ mode: 'create' } | { mode: 'edit'; foreignKey: ForeignKeyModel } | null>(null);

  const describeForeignKey = (fk: ForeignKeyModel) =>
    `${fk.columns.join(', ')} → ${fk.referencedSchema}.${fk.referencedTable}(${fk.referencedColumns.join(', ')})`;

  const applyResult = (result: ForeignKeyDialogResult) => {
    if (dialog?.mode === 'edit') {
      schemaExecute(
        createUpdateForeignKeyCommand(table.id, dialog.foreignKey.id, dialog.foreignKey, {
          ...dialog.foreignKey,
          ...result,
        }),
      );
      setDialog(null);
      return;
    }

    const foreignKey = { id: randomId(), ...result };
    const targetTable = schema.tables.find(
      (candidate) => candidate.schema === result.referencedSchema && candidate.name === result.referencedTable,
    );

    // E7-US3b — stejná logika jako `CanvasView.tsx#handleConnectEnd` (drag&drop),
    // jen druhý vstupní bod (formulář, E4-US8): tabulka, která tímhle FK
    // dostává svou první vazbu, se přemístí blíž k protistraně.
    const placements = targetTable
      ? (() => {
          const tableSizes: Record<string, { width: number; height: number }> = {};
          schema.tables.forEach((candidate) => {
            tableSizes[candidate.id] = { width: TABLE_NODE_WIDTH, height: estimateTableNodeHeight(candidate, canvas.viewSettings) };
          });
          const effectivePositions: Record<string, { x: number; y: number }> = {};
          schema.tables.forEach((candidate, index) => {
            effectivePositions[candidate.id] = canvas.tablePositions[candidate.id] ?? fallbackTablePosition(index);
          });
          return computePlacementsForNewConnection(schema, effectivePositions, tableSizes, table.id, targetTable.id);
        })()
      : [];

    if (placements.length > 0) {
      runGroup({ key: 'schema.command.addForeignKey', params: { name: foreignKey.name } }, () => {
        schemaExecute(createAddForeignKeyCommand(table.id, foreignKey));
        for (const placement of placements) {
          execute(createPlaceTableCommand(placement.tableId, canvas.tablePositions[placement.tableId], placement.position));
        }
      });
    } else {
      schemaExecute(createAddForeignKeyCommand(table.id, foreignKey));
    }
    setDialog(null);
  };

  return (
    <div className="inspector__section">
      <h4>{t('inspector.table.foreignKeys')}</h4>
      <ul className="inspector__column-list">
        {table.foreignKeys.map((fk, index) => (
          <li key={fk.id}>
            <div>
              <span className="inspector__column-name">{fk.name}</span>
              <span className="hint">{describeForeignKey(fk)}</span>
            </div>
            <div className="toolbar__actions">
              <button type="button" onClick={() => setDialog({ mode: 'edit', foreignKey: fk })}>
                <Pencil size={16} />
                {t('inspector.table.edit')}
              </button>
              <button
                type="button"
                className="danger"
                onClick={() => schemaExecute(createDeleteForeignKeyCommand(table.id, fk, index))}
              >
                <Trash2 size={16} />
                {t('common.delete')}
              </button>
            </div>
          </li>
        ))}
      </ul>
      <button type="button" onClick={() => setDialog({ mode: 'create' })}>
        <Plus size={16} />
        {t('inspector.table.addForeignKey')}
      </button>
      {dialog && (
        <ForeignKeyDialog
          schema={schema}
          table={table}
          initial={dialog.mode === 'edit' ? dialog.foreignKey : null}
          onCancel={() => setDialog(null)}
          onConfirm={applyResult}
        />
      )}
    </div>
  );
}

/**
 * Detail FK vazby vybrané na canvasu (Epic 5, E5-US3) — vlastní top-level
 * inspector target (`InspectorTarget.kind === 'foreignKey'`), ne sekce
 * vnořená v `TableInspector` jako `ForeignKeysSection`. Editace jde přes
 * stejný `ForeignKeyDialog` jako `ForeignKeysSection` (žádná duplicitní
 * logika) — jen jiný vstupní bod, přesně dle epic-5 kap. 7 E5-US3.
 */
function ForeignKeyInspector({
  table,
  foreignKey,
  schema,
  canvas,
  execute,
  schemaExecute,
}: {
  table: TableModel;
  foreignKey: ForeignKeyModel;
  schema: SchemaModel;
  canvas: CanvasMetadata;
  execute: (command: CanvasCommand) => void;
  schemaExecute: (command: SchemaCommand) => void;
}) {
  const t = useT();
  const { setInspectorTarget } = useWorkspaceSelection();
  const [editing, setEditing] = useState(false);
  const cardinality = computeForeignKeyCardinality(table, foreignKey);
  const referencingColumn = table.columns.find((candidate) => candidate.name === foreignKey.columns[0]);
  const route = getEdgeRoute(canvas, foreignKey.id);

  return (
    <div className="inspector__body">
      <h3 className="inspector__object-title">{foreignKey.name}</h3>
      <p className="hint">{table.name}</p>

      <dl className="inspector__meta">
        <div>
          <dt>{t('inspector.foreignKey.columns')}</dt>
          <dd>{foreignKey.columns.join(', ')}</dd>
        </div>
        <div>
          <dt>{t('inspector.foreignKey.references')}</dt>
          <dd>
            {foreignKey.referencedSchema}.{foreignKey.referencedTable}({foreignKey.referencedColumns.join(', ')})
          </dd>
        </div>
        <div>
          <dt>{t('inspector.foreignKey.onDelete')}</dt>
          <dd>{foreignKey.onDelete}</dd>
        </div>
        <div>
          <dt>{t('inspector.foreignKey.onUpdate')}</dt>
          <dd>{foreignKey.onUpdate}</dd>
        </div>
        <div>
          <dt>{t('inspector.foreignKey.cardinality')}</dt>
          <dd>{cardinality === 'one' ? '1:1' : '1:N'}</dd>
        </div>
      </dl>

      <div className="toolbar__actions">
        <button type="button" onClick={() => setEditing(true)}>
          <Pencil size={16} />
          {t('inspector.table.edit')}
        </button>
        {referencingColumn && (
          <button
            type="button"
            onClick={() => setInspectorTarget({ kind: 'column', tableId: table.id, columnId: referencingColumn.id })}
          >
            {t('inspector.foreignKey.editCardinalityShortcut')}
            <ArrowRight size={16} />
          </button>
        )}
        {route.isManuallyRouted && (
          <button type="button" onClick={() => execute(createResetEdgeRouteCommand(foreignKey.id, route))}>
            <RotateCcw size={16} />
            {t('canvas.fk.resetRoute')}
          </button>
        )}
        <button
          type="button"
          className="danger"
          onClick={() =>
            schemaExecute(createDeleteForeignKeyCommand(table.id, foreignKey, table.foreignKeys.indexOf(foreignKey)))
          }
        >
          <Trash2 size={16} />
          {t('common.delete')}
        </button>
      </div>

      {editing && (
        <ForeignKeyDialog
          schema={schema}
          table={table}
          initial={foreignKey}
          onCancel={() => setEditing(false)}
          onConfirm={(result) => {
            schemaExecute(
              createUpdateForeignKeyCommand(table.id, foreignKey.id, foreignKey, { ...foreignKey, ...result }),
            );
            setEditing(false);
          }}
        />
      )}
    </div>
  );
}

function IndexesSection({ table, schemaExecute }: { table: TableModel; schemaExecute: (command: SchemaCommand) => void }) {
  const t = useT();
  const [dialog, setDialog] = useState<{ mode: 'create' } | { mode: 'edit'; index: IndexModel } | null>(null);

  return (
    <div className="inspector__section">
      <h4>{t('inspector.table.indexes')}</h4>
      <ul className="inspector__column-list">
        {table.indexes.map((index, position) => (
          <li key={index.id}>
            <div>
              <span className="inspector__column-name">{index.name}</span>
              <span className="hint">
                {index.isUnique ? 'UNIQUE ' : ''}
                {index.columns.length > 0 ? index.columns.join(', ') : index.definition}
              </span>
            </div>
            <div className="toolbar__actions">
              <button type="button" onClick={() => setDialog({ mode: 'edit', index })}>
                <Pencil size={16} />
                {t('inspector.table.edit')}
              </button>
              <button
                type="button"
                className="danger"
                onClick={() => schemaExecute(createDeleteIndexCommand(table.id, index, position))}
              >
                <Trash2 size={16} />
                {t('common.delete')}
              </button>
            </div>
          </li>
        ))}
      </ul>
      <button type="button" onClick={() => setDialog({ mode: 'create' })}>
        <Plus size={16} />
        {t('inspector.table.addIndex')}
      </button>
      {dialog && (
        <IndexDialog
          table={table}
          initial={dialog.mode === 'edit' ? dialog.index : null}
          onCancel={() => setDialog(null)}
          onConfirm={(columns, isUnique, name) => {
            if (dialog.mode === 'edit') {
              schemaExecute(
                createUpdateIndexCommand(table.id, dialog.index.id, dialog.index, {
                  ...dialog.index,
                  columns,
                  isUnique,
                  name,
                }),
              );
            } else {
              schemaExecute(
                createAddIndexCommand(table.id, { id: randomId(), name, columns, isUnique, definition: null }),
              );
            }
            setDialog(null);
          }}
        />
      )}
    </div>
  );
}

function ColumnInspector({
  table,
  column,
  schema,
  canvas,
  execute,
  schemaExecute,
}: SchemaInspectorProps & { table: TableModel; column: ColumnModel }) {
  const t = useT();
  const notes = canvas.notes.filter((note) => note.attachment.type === 'column' && note.attachment.id === column.id);
  const isForeignKey = table.foreignKeys.some((fk) => fk.columns.includes(column.name));
  const catalog = useTypeCatalog(schema.dialect);

  const [name, setName] = useState(column.name);
  useEffect(() => setName(column.name), [column.id, column.name]);
  const [nameError, setNameError] = useState<string | null>(null);
  const [defaultValue, setDefaultValue] = useState(column.defaultValue ?? '');
  useEffect(() => setDefaultValue(column.defaultValue ?? ''), [column.id, column.defaultValue]);
  const [comment, setComment] = useState(column.comment ?? '');
  useEffect(() => setComment(column.comment ?? ''), [column.id, column.comment]);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const editableFields: ColumnEditableFields = {
    rawType: column.rawType,
    canonicalType: column.canonicalType,
    isTypeRecognized: column.isTypeRecognized,
    nullable: column.nullable,
    defaultValue: column.defaultValue,
    comment: column.comment,
  };

  const commitName = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(t('schema.validation.emptyName'));
      setName(column.name);
      return;
    }
    if (trimmed === column.name) {
      setNameError(null);
      return;
    }
    const duplicate = table.columns.some((candidate) => candidate.id !== column.id && candidate.name === trimmed);
    if (duplicate) {
      setNameError(t('inspector.column.duplicateName', { name: trimmed }));
      setName(column.name);
      return;
    }
    setNameError(null);
    schemaExecute(createRenameColumnCommand(table.id, column.id, column.name, trimmed, column.renamedFrom));
  };

  const commitType = (value: ColumnTypeValue) => {
    schemaExecute(createUpdateColumnCommand(table.id, column.id, editableFields, { ...editableFields, ...value }));
  };

  const commitNullable = (nullable: boolean) => {
    schemaExecute(createUpdateColumnCommand(table.id, column.id, editableFields, { ...editableFields, nullable }));
  };

  const commitDefault = () => {
    const next = defaultValue.trim() ? defaultValue : null;
    if (next !== column.defaultValue) {
      schemaExecute(createUpdateColumnCommand(table.id, column.id, editableFields, { ...editableFields, defaultValue: next }));
    }
  };

  const commitComment = () => {
    const next = comment.trim() ? comment.trim() : null;
    if (next !== column.comment) {
      schemaExecute(createUpdateColumnCommand(table.id, column.id, editableFields, { ...editableFields, comment: next }));
    }
  };

  const togglePrimaryKey = (checked: boolean) => {
    const currentColumns = table.primaryKey?.columns ?? [];
    const nextColumns = checked
      ? Array.from(new Set([...currentColumns, column.name]))
      : currentColumns.filter((candidateName) => candidateName !== column.name);
    const nextPrimaryKey: PrimaryKeyModel | null =
      nextColumns.length > 0 ? { name: table.primaryKey?.name ?? null, columns: nextColumns } : null;
    schemaExecute(createSetPrimaryKeyCommand(table.id, table.primaryKey, nextPrimaryKey));
  };

  return (
    <div className="inspector__body">
      <p className="hint">{table.name}</p>
      <div className="field">
        <label htmlFor="inspector-column-name">{t('inspector.column.nameLabel')}</label>
        <input
          id="inspector-column-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={commitName}
          onKeyDown={(event) => {
            if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
          }}
        />
        {nameError && <p className="notice notice--error">{nameError}</p>}
      </div>

      {catalog ? (
        <ColumnTypeEditor catalog={catalog} value={editableFields} onCommit={commitType} />
      ) : (
        <p className="hint">{t('common.loading')}</p>
      )}

      <div className="field">
        <label>
          <input type="checkbox" checked={column.nullable} onChange={(event) => commitNullable(event.target.checked)} />
          {t('inspector.column.nullable')}
        </label>
      </div>

      <div className="field">
        <label htmlFor="inspector-column-default">{t('inspector.column.default')}</label>
        <input
          id="inspector-column-default"
          value={defaultValue}
          onChange={(event) => setDefaultValue(event.target.value)}
          onBlur={commitDefault}
        />
      </div>

      <div className="field">
        <label>
          <input type="checkbox" checked={isPkColumn(table, column)} onChange={(event) => togglePrimaryKey(event.target.checked)} />
          {t('inspector.column.isPrimaryKey')}
        </label>
      </div>

      <dl className="inspector__meta">
        <div>
          <dt>{t('inspector.column.isForeignKey')}</dt>
          <dd>{isForeignKey ? t('common.yes') : t('common.no')}</dd>
        </div>
      </dl>

      <div className="inspector__section">
        <h4>{t('inspector.section.comment')}</h4>
        <textarea value={comment} onChange={(event) => setComment(event.target.value)} onBlur={commitComment} rows={2} />
      </div>

      <div className="inspector__section">
        <button type="button" className="danger" onClick={() => setDeleteOpen(true)}>
          <Trash2 size={16} />
          {t('inspector.column.deleteButton')}
        </button>
      </div>

      <NotesSection notes={notes} canvas={canvas} execute={execute} attachment={{ type: 'column', id: column.id }} />

      {deleteOpen && (
        <DeleteColumnDialog
          table={table}
          column={column}
          schema={schema}
          canvas={canvas}
          schemaExecute={schemaExecute}
          canvasExecute={execute}
          onClose={() => setDeleteOpen(false)}
        />
      )}
    </div>
  );
}

function GroupInspector({
  group,
  schema,
  canvas,
  execute,
}: InspectorContentProps & { group: Group; schema: SchemaModel }) {
  const t = useT();
  const [name, setName] = useState(group.name);
  useEffect(() => setName(group.name), [group.id, group.name]);

  const members = group.memberTableIds
    .map((tableId) => schema.tables.find((table) => table.id === tableId))
    .filter((table): table is TableModel => table !== undefined);
  const notes = canvas.notes.filter((note) => note.attachment.type === 'group' && note.attachment.id === group.id);

  const commitRename = () => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== group.name) execute(createRenameGroupCommand(group.id, group.name, trimmed));
    else setName(group.name);
  };

  return (
    <div className="inspector__body">
      <h3 className="inspector__object-title">{group.name}</h3>

      <div className="field">
        <label htmlFor="inspector-group-name">{t('inspector.group.nameLabel')}</label>
        <input
          id="inspector-group-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={commitRename}
        />
      </div>

      <div className="inspector__section">
        <h4>{t('inspector.group.membersTitle', { count: members.length })}</h4>
        <ul className="inspector__column-list">
          {members.map((table) => (
            <li key={table.id}>
              <span className="inspector__column-name">{table.name}</span>
            </li>
          ))}
        </ul>
      </div>

      <NotesSection notes={notes} canvas={canvas} execute={execute} attachment={{ type: 'group', id: group.id }} />
    </div>
  );
}

function NoteInspector({
  note,
  schema,
  canvas,
  execute,
}: InspectorContentProps & { note: Note; schema: SchemaModel }) {
  const t = useT();
  const attachment = note.attachment;
  let label: string;
  if (attachment.type === 'table') {
    const name = schema.tables.find((table) => table.id === attachment.id)?.name ?? '';
    label = t('inspector.note.attachedToTable', { name });
  } else if (attachment.type === 'group') {
    const name = canvas.groups.find((group) => group.id === attachment.id)?.name ?? '';
    label = t('inspector.note.attachedToGroup', { name });
  } else if (attachment.type === 'column') {
    label = t('inspector.note.attachedToColumn', { name: findColumnLabel(schema, attachment.id) ?? '' });
  } else {
    label = t('inspector.note.free');
  }

  return (
    <div className="inspector__body">
      <h3 className="inspector__object-title">{t('inspector.note.title')}</h3>
      <p className="hint">{label}</p>
      <textarea
        className="note-bubble__text"
        defaultValue={note.text}
        placeholder={t('canvas.note.placeholder')}
        onBlur={(event) => {
          if (event.target.value !== note.text) execute(createEditNoteTextCommand(note.id, note.text, event.target.value));
        }}
      />
      <button
        type="button"
        className="danger"
        onClick={() => execute(createDeleteNoteCommand(note, canvas.notes.findIndex((candidate) => candidate.id === note.id)))}
      >
        <Trash2 size={16} />
        {t('common.delete')}
      </button>
    </div>
  );
}

function NotesSection({
  notes,
  canvas,
  execute,
  attachment,
}: InspectorContentProps & { notes: Note[]; attachment: NoteAttachment }) {
  const t = useT();
  return (
    <div className="inspector__section">
      <h4>{t('inspector.section.notes')}</h4>
      {notes.length === 0 && <p className="hint">{t('inspector.notes.empty')}</p>}
      <div className="stack">
        {notes.map((note) => (
          <NoteEditor key={note.id} note={note} canvas={canvas} execute={execute} />
        ))}
      </div>
      <button
        type="button"
        onClick={() => execute(createNoteCommand({ id: randomId(), text: '', attachment }))}
      >
        <Plus size={16} />
        {t('inspector.notes.add')}
      </button>
    </div>
  );
}

function NoteEditor({ note, canvas, execute }: InspectorContentProps & { note: Note }) {
  const t = useT();
  return (
    <div className="inspector__note">
      <textarea
        className="note-bubble__text"
        defaultValue={note.text}
        placeholder={t('canvas.note.placeholder')}
        onBlur={(event) => {
          if (event.target.value !== note.text) execute(createEditNoteTextCommand(note.id, note.text, event.target.value));
        }}
      />
      <div className="inspector__note-actions">
        <button
          type="button"
          onClick={() =>
            execute(
              createPinNoteCommand(
                note.id,
                { attachment: note.attachment },
                { attachment: { type: 'none' }, position: { x: 0, y: 0 } },
              ),
            )
          }
        >
          <PinOff size={16} />
          {t('canvas.note.unpin')}
        </button>
        <button
          type="button"
          className="danger"
          onClick={() => execute(createDeleteNoteCommand(note, canvas.notes.findIndex((candidate) => candidate.id === note.id)))}
        >
          <Trash2 size={16} />
          {t('common.delete')}
        </button>
      </div>
    </div>
  );
}
